import React, { useState } from 'react';
import Seo from '@/components/Seo';
import { Link, useSearchParams } from 'react-router-dom';
import { Logo } from '@/components/Logo';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Mail, ArrowRight, Copy, Sparkles } from 'lucide-react';

export default function ForgotPassword() {
    const [params] = useSearchParams();
    const [email, setEmail] = useState(params.get('email') || '');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const r = await api.post('/auth/forgot-password', { email, origin: window.location.origin });
            setResult(r.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not send reset link');
        } finally { setLoading(false); }
    };

    return (
        <div className="min-h-dvh bg-[#f4f7fb] flex flex-col">
            <Seo
                title="Reset your password | SplashPad"
                description="Request a password reset link for your SplashPad account."
                canonical="https://splashpad.pro/forgot-password"
                noIndex
            />
            <div className="px-4 py-4 sm:px-6"><Link to="/"><Logo /></Link></div>
            <div className="flex flex-1 items-center justify-center px-4 pb-32 sm:pb-12 sm:px-6">
                <Card className="sd-card w-full max-w-md p-6 sm:p-8">
                    <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#00d4ff]/15">
                        <Mail className="h-5 w-5 text-[#0a1628]" />
                    </div>
                    <h1 className="font-display text-2xl font-bold text-[#0a1628]">Reset your password</h1>
                    <p className="mt-1 text-sm text-[#1a3a5c]/80">Enter the email you used to sign up. We’ll send a reset link that expires in 1 hour.</p>

                    {!result ? (
                        <form onSubmit={submit} className="mt-5 space-y-4" data-testid="forgot-form">
                            <div>
                                <Label htmlFor="em">Email</Label>
                                <Input id="em" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="forgot-email-input" />
                            </div>
                            <Button type="submit" disabled={loading} className="sd-cta sd-focus w-full h-11 rounded-xl" data-testid="forgot-submit-button">
                                {loading ? 'Sending…' : 'Send reset link'} <ArrowRight className="ml-1 h-4 w-4" />
                            </Button>
                        </form>
                    ) : (
                        <div className="mt-5 space-y-4" data-testid="forgot-result">
                            <div className="rounded-xl bg-[#00d4ff]/10 p-4 text-sm text-[#0a1628]">
                                <strong>Check your email.</strong> If an account exists for <span className="font-mono">{email}</span>, we just sent a reset link there.
                            </div>
                            {result.mocked_email && result.reset_link && (
                                <Card className="sd-card border-dashed border-[#ffd700] bg-[#fffbe6] p-4">
                                    <div className="flex items-center gap-2">
                                        <Sparkles className="h-4 w-4 text-[#ffd700]" />
                                        <div className="text-xs font-semibold uppercase tracking-wide text-[#1a3a5c]">Email not yet connected (test mode)</div>
                                    </div>
                                    <p className="mt-2 text-xs text-[#1a3a5c]/80">Until SendGrid/Resend is wired up, click the reset link directly here:</p>
                                    <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                                        <Button asChild className="sd-cta sd-focus rounded-xl flex-1" data-testid="forgot-reset-link-button">
                                            <a href={result.reset_link}>Open reset link <ArrowRight className="ml-1 h-4 w-4" /></a>
                                        </Button>
                                        <Button type="button" variant="outline" className="rounded-xl" onClick={() => { navigator.clipboard.writeText(result.reset_link); toast.success('Link copied'); }} data-testid="forgot-copy-link-button">
                                            <Copy className="mr-1 h-4 w-4" /> Copy
                                        </Button>
                                    </div>
                                </Card>
                            )}
                            <Button variant="ghost" onClick={() => { setResult(null); setEmail(''); }} className="w-full rounded-xl">Use a different email</Button>
                        </div>
                    )}

                    <div className="mt-6 text-sm text-[#1a3a5c]">
                        Remembered it? <Link to="/login" className="font-semibold text-[#0a1628] hover:underline">Back to log in</Link>
                    </div>
                </Card>
            </div>
        </div>
    );
}
